import React, { useState } from 'react';
import axios from 'axios';
import mime from 'mime';
import { BackgroundTag, ListenerRole, TagStatus } from '../../../../hooks/usePeers';
import { useUpdateTag } from '../../../../hooks/useListenerTags';
import { DropDown } from '../../../../components/common';
import { toast } from 'react-toastify';

interface BackgroundTagsReviewProps {
  peerData: ListenerRole;
  refetchPeerData: () => void;
}

const getUrlFileType = (url: string) => {
  const newUrl = new URL(url);
  const ext: string | undefined = newUrl.pathname.split('.').pop();
  return ext === '/' ? undefined : mime.getType(ext?.toLowerCase() ?? '');
};

const openTagMedia = async (file_url: string, file_key: string) => {
  if (file_key === 'VERIFICATION') {
    const authenticateToken = localStorage.getItem('authorization_token');
    const response = await axios.get(file_url, {
      responseType: 'arraybuffer',
      headers: {
        Authorization: 'Bearer ' + authenticateToken,
        'X-Requested-With': 'XMLHTTPRequest',
        Accept: 'application/json',
      },
    });
    const blob = new Blob([response.data], {
      type: getUrlFileType(file_url) || undefined,
    });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    return;
  }
  window.open(file_url, '_blank');
};

interface BackgroundTagRowProps {
  peer: ListenerRole;
  tag: BackgroundTag;
  refetchPeerData: () => void;
}

const BackgroundTagRow: React.FC<BackgroundTagRowProps> = ({ peer, tag, refetchPeerData }) => {
  const { updateTag } = useUpdateTag(peer.id, tag.id);
  const [enableSave, setEnableSave] = useState(false);
  const [tagApprovedStatus, setApprovalStatus] = useState<TagStatus>(tag.status);

  const handleSave = (status: TagStatus) => {
    updateTag.mutate(
      {
        tag_id: tag.tag_id,
        status,
      },
      {
        onSuccess: () => {
          toast.success('Tag updated successfully');
          refetchPeerData();
          setEnableSave(false);
        },
        onError: () => {
          toast.error('Failed to update tag');
          setApprovalStatus(tag.status);
          setEnableSave(false);
        },
      },
    );
  };

  const statusOptions =
    tag.status === 'APPROVED' ? (
      <>
        <option value="APPROVED">Approved</option>
        <option value="UNGRADED">Needs Approval</option>
        <option value="REJECTED">Reject</option>
      </>
    ) : tag.status === 'REJECTED' ? (
      <>
        <option value="REJECTED">Rejected</option>
        <option value="UNGRADED">Needs Approval</option>
        <option value="APPROVED">Approve</option>
      </>
    ) : (
      <>
        <option value="UNGRADED">Needs Approval</option>
        <option value="APPROVED">Approve</option>
        <option value="REJECTED">Reject</option>
      </>
    );

  return (
    <div className="flex flex-col gap-3 rounded border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1 space-y-2">
        <span
          className={`inline-flex items-center rounded px-3 py-1 text-sm font-medium ${
            tag.status === 'APPROVED'
              ? 'bg-green-500 text-white'
              : tag.status === 'REJECTED'
                ? 'bg-red-500 text-white'
                : 'bg-orange-500 text-white'
          }`}
        >
          {tag.tag.name}
        </span>

        <div className="flex flex-col gap-1">
          {tag.media?.length ? (
            tag.media.map((media, idx) => (
              <button
                key={`${tag.id}-${idx}`}
                type="button"
                className="w-full truncate text-left text-sm text-blue-600 underline hover:text-blue-800"
                onClick={() => openTagMedia(media.file_url, media.file_key)}
              >
                {media.file_name || media.file_url}
              </button>
            ))
          ) : (
            <p className="text-xs text-gray-500">No attachment</p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-2 sm:w-48">
        <DropDown
          Label=""
          value={tagApprovedStatus}
          setValue={(value: TagStatus) => {
            setApprovalStatus(value);
            setEnableSave(value !== tag.status);
          }}
        >
          {statusOptions}
        </DropDown>
        <button
          type="button"
          className={`rounded-lg px-3 py-1 text-sm ${
            enableSave ? 'bg-bright-blue text-white' : 'cursor-not-allowed bg-gray-200 text-gray-500'
          }`}
          onClick={() => handleSave(tagApprovedStatus)}
          disabled={!enableSave || updateTag.isLoading}
        >
          Save
        </button>
      </div>
    </div>
  );
};

const BackgroundTagsReview: React.FC<BackgroundTagsReviewProps> = ({ peerData, refetchPeerData }) => {
  if (!peerData.background_tags || peerData.background_tags.length === 0) {
    return <p className="text-xs text-gray-500">No background tags to review.</p>;
  }

  return (
    <div className="space-y-3">
      {peerData.background_tags.map((tag) => (
        <BackgroundTagRow key={tag.id} peer={peerData} tag={tag} refetchPeerData={refetchPeerData} />
      ))}
    </div>
  );
};

export default BackgroundTagsReview;
